import styled, { css } from 'styled-components'
import { media } from 'styles/media'
import typeStyle from 'styles/typeStyle'

interface TextProps {
  uppercase?: boolean
  centered?: boolean
  noMargin?: boolean
  weight?: '500' | '400' | '300'
  styleLink?: boolean
  notResponsive?: boolean
}

const baseTypographyStyles = css<TextProps>`
  ${({ noMargin }) =>
    noMargin &&
    `
    margin-top: 0;
    margin-bottom: 0;
  `}

  ${({ weight }) =>
    weight &&
    `
      font-weight: ${weight};
    `}

    ${media.md<TextProps>`
      text-align: ${({ centered }) => (centered ? 'center' : 'inherit')};
    `}

  ${({ styleLink, theme }) =>
    styleLink &&
    `
    a {
      text-decoration: underline;
      text-underline-offset: 1px;
      text-decoration-thickness: 1px;
      color: ${theme?.['-ds-core-400']?.value};

      &:hover {
        color: ${theme?.['-ds-core-300']?.value};
      }
    }
  `}
`

export const SectionTitle = styled.h3<TextProps>`
  ${typeStyle('-ds-font-body-light')}
  color: ${({ theme }) => theme?.['-ds-core-400']?.value};
  letter-spacing: 0.08em;
  text-transform: uppercase;
  margin-bottom: 8px;

  ${baseTypographyStyles}
`

export const HeadingXxl = styled.h4<TextProps>`
  ${typeStyle('-ds-font-title-1-regular')}
  color: ${({ theme }) => theme?.['-ds-neutral-700']?.value};
  margin-bottom: 24px;

  ${baseTypographyStyles}
`

export const HeadingXl = styled.h4<TextProps>`
  ${typeStyle('-ds-font-title-2-regular')}
  color: ${({ theme }) => theme?.['-ds-neutral-700']?.value};
  margin-bottom: 24px;
  ${baseTypographyStyles}
`

export const HeadingLg = styled.h4<TextProps>`
  ${typeStyle('-ds-font-title-3-regular')}
  color: ${({ theme }) => theme?.['-ds-neutral-700']?.value};
  margin-bottom: 8px;

  ${baseTypographyStyles}
`

export const HeadingMd = styled.h4<TextProps>`
  ${typeStyle('-ds-font-lead')}
  color: ${({ theme }) => theme?.['-ds-neutral-700']?.value};
  margin-bottom: 16px;

  ${baseTypographyStyles}
`

export const HeadingSm = styled.h4<TextProps>`
  ${typeStyle('-ds-font-lead-sm')}
  color: ${({ theme }) => theme?.['-ds-neutral-700']?.value};
  margin-bottom: 16px;

  ${baseTypographyStyles}
`

export const ParagraphLg = styled.p<TextProps>`
  ${typeStyle('-ds-font-lead-light')}
  ${baseTypographyStyles}
  color: ${({ theme }) => theme?.['-ds-neutral-700']?.value};
`

export const ParagraphMd = styled.p<TextProps>`
  ${typeStyle('-ds-font-lead-light')}
  color: ${({ theme }) => theme?.['-ds-neutral-700']?.value};

  ${baseTypographyStyles}

  ${media.md<TextProps>`
    max-width: 640px;
    margin-left: ${({ centered }) => (centered ? 'auto' : '0')};
    margin-right: ${({ centered }) => (centered ? 'auto' : '0')};
  `}
`

export const ParagraphSm = styled.p<TextProps>`
  ${typeStyle('-ds-font-lead-sm-light')}
  color: ${({ theme }) => theme?.['-ds-neutral-700']?.value};
  letter-spacing: 0.01em;

  ${baseTypographyStyles}

  ${media.md<TextProps>`
    max-width: 640px;
    margin-left: ${({ centered }) => (centered ? 'auto' : '0')};
    margin-right: ${({ centered }) => (centered ? 'auto' : '0')};
  `}
`

export const ParagraphXs = styled.p<TextProps>`
  ${({ notResponsive }) =>
    notResponsive
      ? typeStyle('-ds-font-body-light')
      : typeStyle('-ds-font-lead-xs-light')}
  color: ${({ theme }) => theme?.['-ds-neutral-600']?.value};

  ${baseTypographyStyles}
`

export const ParagraphXxs = styled.p<TextProps>`
  ${typeStyle('-ds-font-body-sm-light')}
  text-align: ${({ centered }) => (centered ? 'center' : 'inherit')};
  text-transform: ${({ uppercase }) => (uppercase ? 'uppercase' : 'none')};
  letter-spacing: 0.01em;

  ${baseTypographyStyles}
`

export const ParagraphMicro = styled.p<TextProps>`
  ${typeStyle('-ds-font-caption-sm')}
  text-align: ${({ centered }) => (centered ? 'center' : 'inherit')};
  text-transform: ${({ uppercase }) => (uppercase ? 'uppercase' : 'none')};
  letter-spacing: 0.08em;

  ${baseTypographyStyles}
`

export const StyledLink = styled.a`
  text-decoration: underline;
  text-underline-offset: 1px;
  text-decoration-thickness: 1px;
  color: ${({ theme }) => theme?.['-ds-core-400']?.value};
  background-color: transparent;
  border: none;

  &:hover {
    color: ${({ theme }) => theme?.['-ds-core-300']?.value};
  }
`
